import { useState, useEffect } from 'react'
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
  // GridToolbarFilterButton,
} from '@mui/x-data-grid'

const hiddenColumnsInitially = [
  // 'Country',
  'TechnologyGroup',
  'TSO',
  'BalancingCircle',
  'PlantGroup',
  'ActiveFromTimeUTC',
  'ActiveToTimeUTC',
  'AssociatedMaxPowerKW',
  'BasedOn',
  'insertion_time',
  // 'DahError',
]

const CustomToolbar = ({ selectedSetup }) => {
  return (
    <GridToolbarContainer>
      {/* <GridToolbarFilterButton /> */}
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport
        csvOptions={{
          delimiter: ';',
          fileName: `Portfolio_Overview_${selectedSetup}`,
          utf8WithBom: true,
        }}
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  )
}

const overridesWeirdResizing = {
  '& .MuiDataGrid-main': {
    width: 0,
    minWidth: '100%',
  },
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
  },
  '& .MuiDataGrid-columnHeaders': {
    minHeight: '40px !important',
    maxHeight: '40px !important',
    height: '40px !important',
  },
  '& .MuiDataGrid-footerContainer': {
    minHeight: '40px !important',
    maxHeight: '40px !important',
    height: '40px !important',
  },
  '& .MuiDataGrid-sortIcon': {
    display: 'none',
  },
}

const headerNameMap = {
  InstalledCapacityKW: 'Installed Capacity',
  AssociatedMaxPowerKW: 'AssociatedMaxPower',
  DahError: 'DAH Error',
  insertion_time: 'Insertion Time',
  PlantGroup: 'Plant Group',
  BalancingCircle: 'Balancing Circle',
  TechnologyGroup: 'Technology Group',
  PlantId: 'Plant ID',
  // Add more as needed
}

// Comprehensive column configuration - includes width, behavior, and styling
const columnConfig = {
  Name: {
    flex: 0.9,
    headerName: 'Plant Name',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  PlantId: {
    flex: 0.9,
    headerName: 'Plant ID',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  InstalledCapacityKW: {
    flex: 0.8,
    headerName: 'Installed Capacity',
    type: 'number',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  AssociatedMaxPowerKW: {
    flex: 0.8,
    headerName: 'AssociatedMaxPower',
    type: 'number',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  Country: {
    flex: 0.6,
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  TechnologyGroup: {
    flex: 0.8,
    headerName: 'Technology Group',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  TSO: {
    flex: 0.4,
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  BalancingCircle: {
    flex: 0.8,
    headerName: 'Balancing Circle',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  PlantGroup: {
    flex: 0.7,
    headerName: 'Plant Group',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  Lat: {
    flex: 0.6,
    type: 'number',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  Lon: {
    flex: 0.6,
    type: 'number',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  ActiveFromTimeUTC: {
    flex: 0.9,
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  ActiveToTimeUTC: {
    flex: 0.9,
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  insertion_time: {
    flex: 0.8,
    headerName: 'Insertion Time',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  DahError: {
    flex: 0.7,
    headerName: 'DAH Error',
    type: 'number',
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  BasedOn: {
    flex: 0.8,
    sortable: true,
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
  // Default for any other columns not specified
  default: {
    filterable: true,
    headerAlign: 'left',
    align: 'left',
  },
}

const PortfolioOverviewTable = props => {
  const {
    selectedSetup,
    data,
    selectedPlants,
    setSelectedPlants,
    onFilteredPlantsChange,
    unit,
    isOverviewDAH,
  } = props

  const apiRef = useGridApiRef()

  const filteredData = data.map(obj => {
    if (!isOverviewDAH) {
      // Destructure DahError out and ignore it
      const { DahError, ...rest } = obj
      return rest
    }
    // If admin, keep the object as is
    return obj
  })

  const columns = Object.keys(filteredData[0]).map(key => {
    // Get comprehensive configuration for this column, or use default
    const config = columnConfig[key] || columnConfig.default

    // Use headerName from config, fallback to headerNameMap, then field name
    const headerName = config.headerName || headerNameMap[key] || key

    return {
      field: key,
      headerName,
      ...config, // Spread all configuration (flex, sortable, filterable, etc.)
    }
  })

  const initialColumnVisibilityModel = columns.reduce((acc, obj) => {
    acc[obj.field] = !hiddenColumnsInitially.includes(obj.field)
    return acc
  }, {})

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    initialColumnVisibilityModel,
  )

  // Change time output format
  const formattedData = filteredData.map(obj => {
    const formatDate = timestamp => {
      return new Date(timestamp).toISOString().slice(0, 16).replace('T', ' ')
    }

    const {
      InstalledCapacityKW,
      AssociatedMaxPowerKW,
      DahError,
      ...filteredObj
    } = obj

    return {
      ...filteredObj,
      Lat: obj.Lat.toFixed(3),
      Lon: obj.Lon.toFixed(3),
      ActiveFromTimeUTC: formatDate(obj.ActiveFromTimeUTC),
      ActiveToTimeUTC: formatDate(obj.ActiveToTimeUTC),
      insertion_time: formatDate(obj.insertion_time * 1000),
      InstalledCapacityKW:
        obj.InstalledCapacityKW !== null
          ? parseFloat(
              (unit === 'kW'
                ? obj.InstalledCapacityKW
                : obj.InstalledCapacityKW / 1000
              ).toFixed(2),
            )
          : null,
      AssociatedMaxPowerKW:
        obj.AssociatedMaxPowerKW !== null
          ? parseFloat(
              (unit === 'kW'
                ? obj.AssociatedMaxPowerKW
                : obj.AssociatedMaxPowerKW / 1000
              ).toFixed(2),
            )
          : null,
      ...(DahError !== undefined
        ? {
            DahError:
              DahError !== null
                ? parseFloat(Number(DahError).toFixed(2))
                : null,
          }
        : {}),
    }
  })

  // Track filtered rows using DataGrid API
  useEffect(() => {
    if (!apiRef.current || !onFilteredPlantsChange) return

    const handleFilterChange = () => {
      try {
        // Small delay to ensure grid state is updated after filter change
        setTimeout(() => {
          // Get filtered row IDs using selector (v6 API)
          const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef)

          // Check if filters are actually active
          const filterModel = apiRef.current.state.filter.filterModel
          const hasActiveFilters = filterModel?.items?.length > 0

          if (hasActiveFilters) {
            // Filters active - pass filtered IDs (could be empty array if no matches)
            onFilteredPlantsChange(filteredRowIds)
          } else {
            // No filters - pass null to signal "show all"
            onFilteredPlantsChange(null)
          }
        }, 50)
      } catch (error) {
        console.debug('Grid API not ready yet:', error)
      }
    }

    // Call immediately to set initial state
    handleFilterChange()

    // Subscribe to filter changes
    const unsubscribeFilter = apiRef.current.subscribeEvent(
      'filterModelChange',
      handleFilterChange,
    )

    // Cleanup: unsubscribe when component unmounts or dependencies change
    return () => {
      unsubscribeFilter()
    }
  }, [apiRef, onFilteredPlantsChange])

  return (
    <DataGrid
      apiRef={apiRef}
      rows={formattedData}
      rowHeight={30}
      columns={columns}
      getRowId={row => row.Name}
      checkboxSelection
      rowSelectionModel={selectedPlants}
      onRowSelectionModelChange={setSelectedPlants}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={newModel =>
        setColumnVisibilityModel(newModel)
      }
      // disableColumnMenu
      slots={{
        toolbar: () => <CustomToolbar selectedSetup={selectedSetup} />,
      }}
      sx={overridesWeirdResizing}
    />
  )
}

export default PortfolioOverviewTable
